<ng-container *transloco="let t">
  <div class="flex h-full flex-col items-center justify-center gap-6">
    <div class="max-w-lg space-y-2 text-center">
      <h1 class="text-4xl font-bold md:text-5xl">
        {{ t('reset-password-form-title') }}
      </h1>
      <p>
        {{ t('reset-password-form-subtitle') }}
      </p>
    </div>
    <div class="card bg-base-100 w-full max-w-md shadow-2xl">
      <div class="card-body">
        @if (!passwordReset) {
          <form (ngSubmit)="updatePassword()" [formGroup]="resetPasswordForm">
            <label class="form-control w-full">
              <div class="label">
                <span class="label-text">
                  {{ t('reset-password-form-enter-password') }}
                </span>
              </div>
              <div class="relative w-full">
                <input
                  [attr.aria-invalid]="
                    resetPasswordFormPassword?.invalid &&
                    resetPasswordFormPassword?.touched
                      ? true
                      : null
                  "
                  [type]="getInputType()"
                  autocomplete="on"
                  class="input input-bordered w-full pr-16"
                  formControlName="resetPasswordFormPassword"
                  [ngClass]="{
                    'input-error':
                      resetPasswordFormPassword?.invalid &&
                      resetPasswordFormPassword?.touched,
                  }"
                  placeholder="{{ t('reset-password-form-password') }}"
                />
                <button
                  (click)="toggleShowPassword()"
                  class="btn btn-square btn-ghost absolute right-0"
                  type="button"
                >
                  <svg
                    class="h-6 w-6"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    @if (showPassword) {
                      <ng-container>
                        <path
                          d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </ng-container>
                    } @else {
                      <ng-template *ngTemplateOutlet="EyeIcon"></ng-template>
                    }
                    <ng-template #EyeIcon>
                      <path
                        d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </ng-template>
                  </svg>
                </button>
                @if (
                  resetPasswordFormPassword?.invalid &&
                  resetPasswordFormPassword?.touched
                ) {
                  @if (resetPasswordFormPassword?.errors?.['pattern']) {
                    <p class="label-text-alt text-error">
                      {{ t('reset-password-form-error-password-pattern') }}
                    </p>
                  }
                }
              </div>
            </label>
            <label class="form-control w-full max-w-xl">
              <div class="label">
                <span class="label-text">
                  {{ t('reset-password-form-enter-confirm-password') }}
                </span>
              </div>
              <div class="relative w-full">
                <input
                  [attr.aria-invalid]="
                    resetPasswordFormConfirmPassword?.invalid &&
                    resetPasswordFormConfirmPassword?.touched
                      ? true
                      : null
                  "
                  [type]="getInputType()"
                  autocomplete="on"
                  class="input input-bordered w-full pr-16"
                  formControlName="resetPasswordFormConfirmPassword"
                  [ngClass]="{
                    'input-error':
                      resetPasswordFormConfirmPassword?.invalid &&
                      resetPasswordFormConfirmPassword?.touched,
                  }"
                  placeholder="{{ t('reset-password-form-confirm-password') }}"
                />
                @if (
                  resetPasswordFormConfirmPassword?.invalid &&
                  resetPasswordFormConfirmPassword?.touched
                ) {
                  @if (resetPasswordFormConfirmPassword?.errors?.['pattern']) {
                    <p class="label-text-alt text-error">
                      {{
                        t('reset-password-form-error-confirm-password-pattern')
                      }}
                    </p>
                  }
                  @if (
                    resetPasswordFormConfirmPassword?.errors?.[
                      'passwordsMismatch'
                    ]
                  ) {
                    <p class="label-text-alt text-error">
                      {{
                        t('reset-password-form-error-confirm-password-mismatch')
                      }}
                    </p>
                  }
                }
              </div>
            </label>
            @if (genericError$$ | async) {
              <div
                class="alert alert-error mt-4 flex h-12 max-w-xl items-center"
                [ngClass]="{
                  'opacity-100': showAlert,
                  'opacity-0': !showAlert,
                  'transition-opacity': true,
                  'duration-1000': true,
                  'ease-in-out': true,
                }"
                role="alert"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6 shrink-0 stroke-current"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span class="mx-auto">{{
                  t('reset-password-form-error-generic-error-message')
                }}</span>
              </div>
            }
            <div class="mt-4 flex w-full max-w-xl items-center justify-between">
              <button
                type="submit"
                [class.btn-pulse]="passwordReset"
                [disabled]="passwordReset || resetPasswordForm.invalid"
                class="btn btn-primary m-auto w-full"
              >
                {{ t('reset-password-form-update-password') }}
              </button>
            </div>
          </form>
        } @else {
          <div class="text-center">
            <p class="py-6">
              {{ t('reset-password-form-submitted-success') }}
            </p>
            <button class="btn btn-primary w-full" (click)="goHome()">
              {{ t('reset-password-form-home-button') }}
            </button>
          </div>
        }
      </div>
    </div>
  </div>
</ng-container>
